import { useCallback, useState } from "react";

import { useTherapist } from "../../../context/useTherapist";
import type { Therapist } from "../domain/Therapist";
import { TherapistAPI } from "../infrastructure/api/therapist.api";

/**
 * Saves changes to the signed-in therapist's own account, then reloads it so
 * every screen reading the therapist sees what was stored.
 *
 * For screens outside `user_profile` that edit a setting kept on the account
 * (Payments' notifications, say): they get the state they render — `saving`,
 * `failed` — without reaching into this module's transport.
 *
 * Nothing is reloaded when a screen opens. Sign-in answers with the same
 * stored settings as `GET /therapists/me`, so the context already holds them.
 */
export function useUpdateTherapist() {
    const { therapist, refreshTherapist } = useTherapist();
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    const update = useCallback(
        async (changes: Partial<Therapist>): Promise<boolean> => {
            if (!therapist) return false;
            setSaving(true);
            setFailed(false);
            try {
                await TherapistAPI.update(therapist.id, changes);
                await refreshTherapist?.();
                return true;
            } catch {
                setFailed(true);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [therapist, refreshTherapist]
    );

    return { therapist, update, saving, failed };
}
